import { defineComponent as _defineComponent } from 'vue'
import Job from '@/models/job'
import { computed, reactive, UnwrapNestedRefs } from 'vue'
import moment from 'moment'
import FormLayout from '@/components/common/FormLayout.vue'
import { formatDurationAsHours } from '@/utils/date-formatting'
import { createNamespacedHelpers } from 'vuex-composition-helpers'

type Data = UnwrapNestedRefs<{
  saving: boolean
  endTime: moment.Moment
  completedTime: moment.Moment
  activeTime: moment.Moment
  startTime: moment.Moment
  isSnackbarVisible: boolean
  error: boolean
}>

interface CompletedJobTimesPayload {
  id: string
  startTime: moment.Moment
  endTime: moment.Moment
  activeTime: moment.Moment
  completedTime: moment.Moment
}


export default /*#__PURE__*/_defineComponent({
  __name: 'JobPlannedActualTimesEdit',
  props: {
    job: null,
    jobHubId: null
  },
  emits: ["dismiss", "updated"],
  setup(__props: any, { emit: emits }: { emit: ({
  (event: 'dismiss'): void
  (event: 'updated'): void
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  job: Job
  jobHubId: string
}



const data: Data = reactive({
  startTime: props.job.startTime,
  endTime: props.job.endTime,
  activeTime: moment(props.job.activeTime),
  completedTime: moment(props.job.completedTime),
  saving: false,
  isSnackbarVisible: false,
  error: false,
})

const { updateCompletedJobActualTimes } = createNamespacedHelpers(
  'jobs'
).useActions(['updateCompletedJobActualTimes'])



const hasChanges = computed<boolean>(() => {
  return (
    !data.startTime.isSame(props.job.startTime) ||
    !data.endTime.isSame(props.job.endTime) ||
    !data.activeTime.isSame(moment(props.job.activeTime)) ||
    !data.completedTime.isSame(moment(props.job.completedTime))
  )
})

const showStartTimeWarning = computed<boolean>(() => {
  return (
    data.startTime.isAfter(data.endTime) &&
    !data.startTime.isSame(props.job.startTime)
  )
})

const showEndTimeWarning = computed<boolean>(() => {
  return (
    data.endTime.isBefore(data.startTime) &&
    !data.endTime.isSame(props.job.endTime)
  )
})

const showActivatedTimeWarning = computed<boolean>(() => {
  return (
    data.activeTime.isAfter(data.completedTime) &&
    !data.activeTime.isSame(moment(props.job.activeTime))
  )
})

const showCompletedTimeWarning = computed<boolean>(() => {
  return (
    data.completedTime.isBefore(data.activeTime) &&
    !data.completedTime.isSame(moment(props.job.completedTime))
  )
})

const showNaNWarning = computed<boolean>(() => {
  return (
    isNaN(data.startTime.valueOf()) ||
    isNaN(data.endTime.valueOf()) ||
    isNaN(data.activeTime.valueOf()) ||
    isNaN(data.completedTime.valueOf())
  )
})

const saveButtonDisabled = computed<boolean>(() => {
  return (
    !hasChanges.value ||
    showStartTimeWarning.value ||
    showEndTimeWarning.value ||
    showNaNWarning.value ||
    showActivatedTimeWarning.value ||
    showCompletedTimeWarning.value
  )
})

const formattedPlannedDuration = computed<string>(() => {
  if (
    isNaN(data.endTime.valueOf()) ||
    isNaN(data.startTime.valueOf()) ||
    showStartTimeWarning.value ||
    showEndTimeWarning.value
  ) {
    return '\u{2E3A}'
  }
  const estimatedDuration = moment.duration(data.endTime.diff(data.startTime))
  return formatDurationAsHours(estimatedDuration, true)
})

const formattedActiveDuration = computed<string>(() => {
  if (
    isNaN(data.activeTime.valueOf()) ||
    isNaN(data.completedTime.valueOf()) ||
    showActivatedTimeWarning.value ||
    showCompletedTimeWarning.value
  ) {
    return '\u{2E3A}'
  }
  const newDuration = moment.duration(data.completedTime.diff(data.activeTime))
  return formatDurationAsHours(newDuration, true)
})

const plannedDurationLabel = computed<string>(() => {
  return `Duration: ${formattedPlannedDuration.value}`
})

const activeDurationLabel = computed<string>(() => {
  return `Duration: ${formattedActiveDuration.value}`
})

const completionLabel = computed<string>(() => {
  return !props.job.activeTime ||
    moment(props.job.activeTime).isSame(
      moment(props.job.completedTime),
      'minute'
    )
    ? 'Completed'
    : 'Executed'
})

const handleStartTimeChange = (value: moment.Moment): void => {
  data.startTime = value
}

const handleEndTimeChange = (value: moment.Moment): void => {
  data.endTime = value
}

const handleActivatedTimeChange = (value: moment.Moment): void => {
  data.activeTime = value
}

const handleCompletedTimeChange = (value: moment.Moment): void => {
  data.completedTime = value
}

const save = async (): Promise<void> => {
  data.saving = true
  try {
    const updateCompletedJobActualTimesPayload: CompletedJobTimesPayload = {
      id: props.job.uuid,
      startTime: data.startTime,
      endTime: data.endTime,
      activeTime: data.activeTime,
      completedTime: data.completedTime,
    }
    await updateCompletedJobActualTimes(updateCompletedJobActualTimesPayload)
    emits('updated')
  } catch (err) {
    data.error = true
    data.saving = false
    return
  } finally {
    data.saving = false
    emits('dismiss')
  }
}

const dismiss = (): void => {
  emits('dismiss')
}

return { __sfc: true,props, data, updateCompletedJobActualTimes, emits, hasChanges, showStartTimeWarning, showEndTimeWarning, showActivatedTimeWarning, showCompletedTimeWarning, showNaNWarning, saveButtonDisabled, formattedPlannedDuration, formattedActiveDuration, plannedDurationLabel, activeDurationLabel, completionLabel, handleStartTimeChange, handleEndTimeChange, handleActivatedTimeChange, handleCompletedTimeChange, save, dismiss, FormLayout }
}

})